import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {RoomPlannerProductWithTemplate} from 'components/customer/RoomPlanner/types';

/**
 * Prepares L-shaped cabinet dimension values
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {boolean} isLShapedFootprint - Whether cabinet has L-shaped footprint
 * @return {Partial<RoomPlannerProductWithTemplate>} L-shaped dimension values
 */
const prepareLShapedValues = (
    values: RoomPlannerProductWithTemplate,
    isLShapedFootprint: boolean
) => {
    if (!isLShapedFootprint) {
        return {};
    }

    return {
        cabinet_left_width: Number(values.cabinet_left_width ?? values.width2),
        cabinet_right_width: Number(values.cabinet_right_width ?? values.width),
        cabinet_right_depth: Number(
            values.cabinet_right_depth ??
                values.cabinet_length_depth ??
                values.length_depth
        ),
        cabinet_left_depth: Number(
            values.cabinet_left_depth ??
                values.cabinet_width_depth ??
                values.width_depth
        ),
    };
};

/**
 * Prepares key shape panel dimension values
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {TemplateVariable} variables - Template variables
 * @return {Partial<RoomPlannerProductWithTemplate>} Key shape dimension values
 */
const prepareKeyShapeValues = (
    values: RoomPlannerProductWithTemplate,
    variables: TemplateVariable
) => {
    if (!variables.isKeyShapePanel) {
        return {};
    }

    return {
        cabinet_left_width: Number(values.cabinet_panel_width),
        cabinet_right_depth: Number(values.cabinet_length_depth),
        cabinet_right_width: Number(values.cabinet_panel_length),
        cabinet_left_depth: Number(values.cabinet_width_depth),
    };
};

/**
 * Merges all cabinet values with L-shaped and key shape values
 * @param {RoomPlannerProductWithTemplate} values - Product values
 * @param {TemplateVariable} variables - Template variables
 * @param {boolean} isLShapedFootprint - Whether cabinet has L-shaped footprint
 * @param {string} templateName - Template name
 * @return {RoomPlannerProductWithTemplate} Merged cabinet values
 */
export const mergeCabinetValues = (
    values: RoomPlannerProductWithTemplate,
    variables: TemplateVariable,
    isLShapedFootprint: boolean,
    templateName: string
): RoomPlannerProductWithTemplate => {
    const lShapedValues = prepareLShapedValues(values, isLShapedFootprint);
    const keyShapeValues = prepareKeyShapeValues(values, variables);
    const isInnerDrawer = templateName?.endsWith('InnerDrawer.xml');

    return {
        ...values,
        ...lShapedValues,
        ...keyShapeValues,
        isInnerDrawer,
    } as RoomPlannerProductWithTemplate;
};
